/**
 * Unified Coordinate System Utilities for 360Crafter
 *
 * This module provides consistent coordinate system conversions between:
 * - Three.js internal coordinates (radians)
 * - Display coordinates (degrees, user-friendly)
 * - 3D world positions (x, y, z)
 *
 * All functions use the same coordinate system conventions:
 * - Yaw: 0° = forward (-Z direction), positive = right (+X direction)
 * - Pitch: 0° = horizontal, positive = down, negative = up
 * - Camera forward direction: -Z
 */

import * as THREE from "three";

// Constants for coordinate system
export const COORDINATE_SYSTEM = {
  CAMERA_FORWARD: new THREE.Vector3(0, 0, -1), // -Z direction
  CAMERA_RIGHT: new THREE.Vector3(1, 0, 0), // +X direction
  CAMERA_UP: new THREE.Vector3(0, 1, 0), // +Y direction
} as const;

/**
 * Convert 3D position to yaw/pitch angles
 * @param position 3D position {x, y, z}
 * @returns {yaw, pitch} in degrees
 */
export function positionToYawPitch(position: { x: number; y: number; z: number }): {
  yaw: number;
  pitch: number;
} {
  const { x, y, z } = position;

  // Calculate yaw: angle from -Z (forward) to +X (right)
  const yaw = Math.atan2(x, -z) * (180 / Math.PI);

  // Calculate pitch: angle from horizontal plane to position
  const pitch = -Math.atan2(y, Math.sqrt(x * x + z * z)) * (180 / Math.PI);

  return { yaw, pitch };
}

/**
 * Convert yaw/pitch angles to 3D position
 * @param yaw Yaw angle in degrees
 * @param pitch Pitch angle in degrees
 * @param distance Distance from origin (default: 480)
 * @returns 3D position {x, y, z}
 */
export function yawPitchToPosition(
  yaw: number,
  pitch: number,
  distance: number = 480,
): { x: number; y: number; z: number } {
  const yawRad = yaw * (Math.PI / 180);
  const pitchRad = -pitch * (Math.PI / 180); // Negate to match coordinate system

  const x = distance * Math.cos(pitchRad) * Math.sin(yawRad);
  const y = distance * Math.sin(pitchRad);
  const z = -distance * Math.cos(pitchRad) * Math.cos(yawRad); // Negative for camera forward = -Z

  return { x, y, z };
}

/**
 * Convert Three.js camera rotation to display coordinates
 * @param camera Three.js camera
 * @returns {yaw, pitch, roll} in degrees
 */
export function cameraToDisplayCoords(camera: THREE.Camera): {
  yaw: number;
  pitch: number;
  roll: number;
} {
  const euler = new THREE.Euler().setFromQuaternion(camera.quaternion, "YXZ");
  const deg = THREE.MathUtils.radToDeg;

  // Apply consistent sign conventions
  const yaw = -deg(euler.y);
  // Display pitch is positive when looking down, negative when looking up.
  const pitch = -deg(euler.x);
  const roll = deg(euler.z);

  return {
    yaw: normalizeAngle(yaw),
    pitch: THREE.MathUtils.clamp(pitch, -90, 90),
    roll: normalizeAngle(roll),
  };
}

/**
 * Convert display coordinates to Three.js camera rotation
 * @param yaw Yaw in degrees
 * @param pitch Pitch in degrees
 * @returns Three.js rotation {x, y, z} in radians
 */
export function displayCoordsToCamera(
  yaw: number,
  pitch: number,
): { x: number; y: number; z: number } {
  return {
    x: -pitch * (Math.PI / 180),
    y: -yaw * (Math.PI / 180),
    z: 0,
  };
}

/**
 * Apply display coordinates to Three.js camera
 * @param camera Three.js camera
 * @param yaw Yaw in degrees
 * @param pitch Pitch in degrees
 */
export function applyDisplayCoordsToCamera(camera: THREE.Camera, yaw: number, pitch: number): void {
  const rotation = displayCoordsToCamera(yaw, pitch);

  camera.rotation.x = rotation.x;
  camera.rotation.y = rotation.y;
  camera.rotation.z = rotation.z;

  // Clamp pitch to prevent flipping
  camera.rotation.x = THREE.MathUtils.clamp(camera.rotation.x, -Math.PI / 2, Math.PI / 2);
}

/**
 * Focus camera on a 3D position
 * @param camera Three.js camera
 * @param position 3D position {x, y, z}
 */
export function focusCameraOnPosition(
  camera: THREE.Camera,
  position: { x: number; y: number; z: number },
): void {
  const { yaw, pitch } = positionToYawPitch(position);
  applyDisplayCoordsToCamera(camera, yaw, pitch);
}

/**
 * Normalize angle to (-180, 180] range
 * @param angle Angle in degrees
 * @returns Normalized angle
 */
export function normalizeAngle(angle: number): number {
  while (angle > 180) angle -= 360;
  while (angle <= -180) angle += 360;
  return angle;
}

/**
 * Calculate shortest rotation path between two angles
 * @param from Starting angle in degrees
 * @param to Target angle in degrees
 * @returns Shortest rotation difference in degrees
 */
export function shortestRotation(from: number, to: number): number {
  const diff = to - from;
  return normalizeAngle(diff);
}

/**
 * Convert 3D position to 2D screen coordinates
 * @param position 3D position {x, y, z}
 * @param camera Three.js camera
 * @param screenWidth Screen width
 * @param screenHeight Screen height
 * @returns 2D screen coordinates {x, y} as percentages (0-100)
 */
export function positionToScreenCoords(
  position: { x: number; y: number; z: number },
  camera: THREE.Camera,
  _screenWidth: number,
  _screenHeight: number,
): { x: number; y: number } {
  const vector = new THREE.Vector3(position.x, position.y, position.z);
  vector.project(camera);

  // Convert from normalized device coordinates (-1 to 1) to percentage (0 to 100)
  const x = ((vector.x + 1) / 2) * 100;
  const y = ((vector.y + 1) / 2) * 100;

  return {
    x: Math.max(0, Math.min(100, x)),
    y: Math.max(0, Math.min(100, y)),
  };
}
